import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import { useToast, Spinner } from '@chakra-ui/react';
import {
    getProjectCollaborators,
    inviteCollaborator,
    updateCollaboratorPermission,
    revokeCollaboratorAccess
} from '../../dashboardapi';
import styles from './ProjectModal.module.css';

const ShareModal = ({ show, onClose, project }) => {
    const [collaborators, setCollaborators] = useState([]);
    const [email, setEmail] = useState("");
    const [permission, setPermission] = useState("read");
    const [loading, setLoading] = useState(false);
    const [inviteLoading, setInviteLoading] = useState(false);
    const toast = useToast();

    useEffect(() => {
        if (show && project) {
            fetchCollaborators();
        }
    }, [show, project]);

    const fetchCollaborators = async () => {
        setLoading(true);
        try {
            const result = await getProjectCollaborators(project.uuid);
            if (result.data && result.data.shares) {
                setCollaborators(result.data.shares);
            } else if (result.error) {
                console.error("Error fetching collaborators:", result.error);
                // Optional: show toast if needed, but might be annoying on load
            }
        } catch (error) {
            console.error("Error fetching collaborators:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleInvite = async (e) => {
        e.preventDefault();
        setInviteLoading(true);
        try {
            const result = await inviteCollaborator(project.uuid, email, permission);
            if (result.data) {
                toast({
                    title: "Invitation sent",
                    status: "success",
                    duration: 3000,
                    isClosable: true,
                });
                setEmail("");
                fetchCollaborators(); // Refresh list
            } else if (result.error) {
                const errorMsg = result.error.detail || result.error.message || "Failed to invite user";
                toast({
                    title: "Failed to invite",
                    description: errorMsg,
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                });
            }
        } catch (error) {
            console.error(error);
            toast({
                title: "Error inviting user",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setInviteLoading(false);
        }
    };

    const handlePermissionChange = async (shareId, newPermission) => {
        try {
            const result = await updateCollaboratorPermission(project.uuid, shareId, newPermission);
            if (result.data) {
                toast({
                    title: "Permission updated",
                    status: "success",
                    duration: 2000,
                    isClosable: true,
                });
                fetchCollaborators();
            } else if (result.error) {
                toast({
                    title: "Error updating permission",
                    description: result.error.detail || "Unknown error",
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                });
            }
        } catch (error) {
            toast({
                title: "Error updating permission",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        }
    };

    const handleRevoke = async (shareId) => {
        try {
            const result = await revokeCollaboratorAccess(project.uuid, shareId);
            if (result.data) {
                toast({
                    title: "Access revoked",
                    status: "success",
                    duration: 2000,
                    isClosable: true,
                });
                fetchCollaborators();
            } else if (result.error) {
                toast({
                    title: "Error revoking access",
                    description: result.error.detail || "Unknown error",
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                });
            }
        } catch (error) {
            toast({
                title: "Error revoking access",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        }
    }

    return (
        <Modal show={show}>
            <div className={styles.modalContainer}>
                <div className={styles.modalheadingContainer}>
                    <p>Share "{project?.name}"</p>
                    <button onClick={onClose}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </button>
                </div>
                <div className={styles.modalContentContainer}>
                    <div className={styles.formouter}>
                        <div className={styles.inputdiv}>
                            <label>Invite User</label>
                            <label style={{ fontSize: '12px', color: '#666', marginTop: '-5px', marginBottom: '8px' }}>
                                Enter the email address of the user you want to share this project with.
                            </label>
                            <div style={{ display: 'flex', gap: '10px' }}>
                                <input
                                    type="email"
                                    placeholder="Enter email address"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    style={{ flex: 1 }}
                                />
                                <select
                                    value={permission}
                                    onChange={(e) => setPermission(e.target.value)}
                                    style={{ width: '100px', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                                >
                                    <option value="read">Read</option>
                                    <option value="write">Write</option>
                                    {/* <option value="admin">Admin</option> */}
                                </select>
                                <button
                                    className={styles.btn2}
                                    onClick={handleInvite}
                                    disabled={!email || inviteLoading}
                                    style={{ width: 'auto', padding: '0 20px' }}
                                >
                                    {inviteLoading ? <Spinner size="sm" /> : "Invite"}
                                </button>
                            </div>
                        </div>

                        <div className={styles.inputdiv} style={{ marginTop: '20px' }}>
                            <label>Collaborators</label>
                            <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #eee', borderRadius: '4px', padding: '10px' }}>
                                {loading ? (
                                    <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                                        <Spinner />
                                    </div>
                                ) : collaborators.length === 0 ? (
                                    <p style={{ color: '#888', textAlign: 'center' }}>No collaborators yet.</p>
                                ) : (
                                    collaborators.map((collab) => (
                                        <div key={collab.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', paddingBottom: '10px', borderBottom: '1px solid #f9f9f9' }}>
                                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                <span style={{ fontWeight: '500' }}>{collab.user_email}</span>
                                                <span style={{ fontSize: '12px', color: '#888' }}>
                                                    {collab.status === 'pending' ? '(Pending Invite)' : ''}
                                                    {collab.status === 'declined' ? '(Declined)' : ''}
                                                </span>
                                            </div>
                                            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                                <select
                                                    value={collab.permission}
                                                    onChange={(e) => handlePermissionChange(collab.id, e.target.value)}
                                                    disabled={collab.status === 'pending' || collab.status === 'declined'}
                                                    style={{ padding: '4px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '12px' }}
                                                >
                                                    <option value="read">Read</option>
                                                    <option value="write">Write</option>
                                                    {/* <option value="admin">Admin</option> */}
                                                </select>
                                                <button
                                                    onClick={() => handleRevoke(collab.id)}
                                                    style={{ color: 'white', background: '#e53e3e', border: 'none', borderRadius: '4px', padding: '4px 8px', cursor: 'pointer', fontSize: '12px' }}
                                                >
                                                    Remove
                                                </button>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default ShareModal;
